import React, { useState, useRef, useCallback } from "react";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  Sparkles,
  AlertCircle,
  Loader2,
  Undo2,
  TriangleAlert,
  ArrowLeft,
  Download,
  SkipForward,
} from "lucide-react";
import { dataImportApi } from "../../../services/api";
import SearchableSelect from "../../ui/SearchableSelect";

// ─── Types ──────────────────────────────────────────────────────────────────

interface ImportDataStepProps {
  importDone: boolean;
  onImportComplete: () => void;
  onSkip?: () => void;
}

type EntityType = "clients" | "contacts";
type Step = "upload" | "preview" | "success";

interface EntityColumnMapping {
  [field: string]: number;
}

interface PreviewData {
  headers: string[];
  sampleRows: string[][];
  totalRows: number;
  mapping: EntityColumnMapping;
  entityType: EntityType;
}

interface ImportResult {
  batchId: string;
  imported: number;
  duplicates: number;
  skippedRows: number;
  errors: string[];
}

// ─── Field Definitions ──────────────────────────────────────────────────────

const CLIENT_FIELDS = [
  { value: "displayName", label: "Name", required: true },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "type", label: "Type (individual/organization)" },
  { value: "tags", label: "Tags" },
  { value: "notes", label: "Notes" },
  { value: "address", label: "Address" },
  { value: "city", label: "City" },
  { value: "province", label: "Province / State" },
  { value: "postal", label: "Postal Code" },
  { value: "matterTitle", label: "Matter / File Name" },
  { value: "matterNumber", label: "Matter/File #" },
  { value: "matterPracticeArea", label: "Matter Practice Area" },
  { value: "billingType", label: "Billing Type (hourly/flat/contingency)" },
];

const CONTACT_FIELDS = [
  { value: "displayName", label: "Name", required: true },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "company", label: "Company" },
  { value: "role", label: "Role / Title" },
  { value: "category", label: "Category" },
  { value: "notes", label: "Notes" },
];

// ─── Component ──────────────────────────────────────────────────────────────

const ImportDataStep: React.FC<ImportDataStepProps> = ({
  importDone,
  onImportComplete,
  onSkip,
}) => {
  const [entityType, setEntityType] = useState<EntityType>("clients");
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [mapping, setMapping] = useState<EntityColumnMapping | null>(null);
  const [autoDetectedFields] = useState<Set<string>>(new Set());
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showMappingOverride, setShowMappingOverride] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fields = entityType === "clients" ? CLIENT_FIELDS : CONTACT_FIELDS;

  // How many fields got auto-mapped
  const autoMappedCount = mapping
    ? Object.entries(mapping).filter(([, idx]) => idx >= 0).length
    : 0;

  // Is the required "displayName" field mapped?
  const displayNameMapped = mapping
    ? mapping.displayName !== undefined && mapping.displayName >= 0
    : false;

  // Likely wrong file if only 0-1 columns detected
  const isLikelyWrongFile = preview !== null && autoMappedCount <= 1;

  // Column options for the mapping dropdowns
  const columnOptions = preview
    ? [
        { value: "-1", label: "(Skip)" },
        ...preview.headers.map((h, i) => ({
          value: String(i),
          label: `${i + 1}. ${h}`,
        })),
      ]
    : [];

  // Mapped column indices (for highlighting in preview table)
  const mappedColumns = new Set(
    Object.values(mapping || {}).filter((v) => v >= 0)
  );

  // ── File handling ───────────────────────────────────────────────────────

  const handleFile = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile);
      setError("");
      setLoading(true);
      setShowMappingOverride(false);

      try {
        const formData = new FormData();
        formData.append("file", selectedFile);

        const previewFn =
          entityType === "clients"
            ? dataImportApi.previewClients
            : dataImportApi.previewContacts;

        const res = await previewFn(formData);
        const data = res.data as PreviewData;

        setPreview(data);
        setMapping(data.mapping);

        // Track which fields were auto-detected
        const detected = new Set<string>();
        for (const [key, idx] of Object.entries(data.mapping)) {
          if (idx >= 0) detected.add(key);
        }
        // Replace contents of autoDetectedFields set
        autoDetectedFields.clear();
        detected.forEach((f) => autoDetectedFields.add(f));

        setStep("preview");
      } catch (err: any) {
        setError(
          err.response?.data?.error || "Failed to parse CSV file. Check that it's a valid CSV."
        );
      } finally {
        setLoading(false);
      }
    },
    [entityType, autoDetectedFields]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFile(droppedFile);
    },
    [handleFile]
  );

  // ── Import ──────────────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!file || !mapping) return;
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mapping", JSON.stringify(mapping));
      formData.append("skipDuplicates", String(skipDuplicates));

      const importFn =
        entityType === "clients"
          ? dataImportApi.importClients
          : dataImportApi.importContacts;

      const res = await importFn(formData);
      setImportResult(res.data as ImportResult);
      setStep("success");
      onImportComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to import data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Undo ────────────────────────────────────────────────────────────────

  const handleUndo = async () => {
    if (!importResult?.batchId) return;
    setUndoing(true);
    try {
      await dataImportApi.undoBatch(entityType, importResult.batchId);
      setUndone(true);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to undo import");
    } finally {
      setUndoing(false);
    }
  };

  // ── Reset to upload ─────────────────────────────────────────────────────

  const resetToUpload = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setMapping(null);
    setError("");
    setShowMappingOverride(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadTemplate = () => {
    const headers = fields.map((f) => f.label);
    // Add a sample row so users see the expected format
    const sampleRow =
      entityType === "clients"
        ? ["Jane Smith", "Jane", "Smith", "jane@example.com", "(416) 555-1234", "individual", "Real Estate", "Referred by John Doe", "123 Bay St", "Toronto", "Ontario", "M5J 2T3", "Smith v. Jones", "2024-001", "Litigation", "hourly"]
        : ["Jane Smith", "Jane", "Smith", "jane@example.com", "(416) 555-1234", "Acme Corp", "Accountant", "Referral Partner", "Met at conference"];
    const csv = [headers.join(","), sampleRow.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `kavoa-${entityType}-template.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Success state ───────────────────────────────────────────────────────

  if (importDone || step === "success") {
    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-900">Import your data</h2>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-400 leading-none">
              Optional
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Bring in your existing clients and contacts from a CSV file.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              Import Complete
            </h3>
            {importResult ? (
              <>
                <p className="text-sm text-slate-600 mb-1">
                  Successfully imported{" "}
                  <span className="font-bold text-emerald-700">
                    {importResult.imported}
                  </span>{" "}
                  {entityType === "clients" ? "clients" : "contacts"}.
                </p>
                {importResult.duplicates > 0 && (
                  <p className="text-sm text-amber-600 mb-1">
                    {importResult.duplicates} duplicates skipped
                  </p>
                )}
                {importResult.skippedRows > 0 && (
                  <p className="text-sm text-amber-600 mb-1">
                    {importResult.skippedRows} rows skipped (invalid data)
                  </p>
                )}
                {importResult.errors.length > 0 && (
                  <div className="mt-3 text-left bg-amber-50 rounded-lg p-3 max-w-md mx-auto">
                    <p className="text-xs font-medium text-amber-700 mb-1">
                      Warnings:
                    </p>
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <p key={i} className="text-xs text-amber-600">
                        {err}
                      </p>
                    ))}
                    {importResult.errors.length > 5 && (
                      <p className="text-xs text-amber-500 mt-1">
                        ...and {importResult.errors.length - 5} more
                      </p>
                    )}
                  </div>
                )}
                {!undone && importResult.imported > 0 && (
                  <button
                    onClick={handleUndo}
                    disabled={undoing}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {undoing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Undo2 className="h-4 w-4" />
                    )}
                    {undoing ? "Undoing..." : "Undo Import"}
                  </button>
                )}
                {undone && (
                  <p className="mt-3 text-sm text-emerald-600 font-medium">
                    Import has been undone successfully.
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">
                Your data has been imported successfully.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Preview/Mapping step ────────────────────────────────────────────────

  if (step === "preview" && preview && mapping) {
    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-900">Import your data</h2>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-400 leading-none">
              Optional
            </span>
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-slate-100 to-slate-50 text-slate-500 border border-slate-200 hover:from-slate-200 hover:to-slate-100 hover:text-slate-700 hover:border-slate-300 shadow-sm hover:shadow transition-all"
              >
                <SkipForward className="h-3.5 w-3.5" />
                Skip this step
              </button>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Review and adjust the column mapping before importing.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* File info bar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
          <FileSpreadsheet className="w-5 h-5 text-brand flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-slate-700 truncate block">
              {file?.name}
            </span>
          </div>
          <span className="text-xs text-slate-400">
            {file ? formatFileSize(file.size) : ""}
          </span>
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-md">
            {preview.totalRows} rows
          </span>
        </div>

        {/* Wrong-file warning */}
        {isLikelyWrongFile && !showMappingOverride ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <TriangleAlert className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-amber-800 mb-1">
                  No matching columns found
                </h3>
                <p className="text-sm text-amber-700 mb-1">
                  Your file headers ({preview.headers.slice(0, 4).join(", ")}
                  {preview.headers.length > 4 ? ", ..." : ""}) don't appear to
                  match {entityType === "clients" ? "client" : "contact"} fields.
                </p>
                <p className="text-sm text-amber-600 mb-4">
                  This might be a billing, financial, or other type of CSV. You can
                  map columns manually or try a different file.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowMappingOverride(true)}
                    className="px-4 py-2 rounded-xl text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                  >
                    Map Columns Manually
                  </button>
                  <button
                    onClick={resetToUpload}
                    className="px-4 py-2 rounded-xl text-sm font-medium border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors"
                  >
                    Try a Different File
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Mapping quality banner */}
            {autoMappedCount >= 3 ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-brand-light border border-brand-light rounded-xl">
                <CheckCircle className="w-5 h-5 text-brand-accent flex-shrink-0" />
                <span className="text-sm text-brand">
                  Auto-detected <strong>{autoMappedCount}</strong> of{" "}
                  {fields.length} columns. Review the mapping below.
                </span>
              </div>
            ) : autoMappedCount >= 1 ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
                <TriangleAlert className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <span className="text-sm text-amber-700">
                  Only <strong>{autoMappedCount}</strong> column
                  {autoMappedCount === 1 ? " was" : "s were"} auto-detected.
                  Please review and adjust the mapping below.
                </span>
              </div>
            ) : null}

            {/* Column Mapping Grid */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                Column Mapping
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields.map((field) => {
                  const colIdx = mapping[field.value];
                  const isAutoDetected = autoDetectedFields.has(field.value);
                  const isMapped = colIdx !== undefined && colIdx >= 0;

                  // Get sample values for the mapped column
                  const sampleValues =
                    isMapped && preview.sampleRows.length > 0
                      ? preview.sampleRows
                          .slice(0, 2)
                          .map((row) => row[colIdx] || "")
                          .filter(Boolean)
                      : [];

                  return (
                    <div key={field.value}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            isMapped && isAutoDetected
                              ? "bg-brand-accent"
                              : isMapped
                              ? "bg-blue-400"
                              : "bg-slate-200"
                          }`}
                        />
                        <label className="text-xs text-slate-500 font-medium">
                          {field.label}
                          {field.required && (
                            <span className="text-red-400 ml-0.5">*</span>
                          )}
                        </label>
                      </div>
                      <SearchableSelect
                        options={columnOptions}
                        value={
                          colIdx !== undefined ? String(colIdx) : "-1"
                        }
                        onChange={(val) =>
                          setMapping({
                            ...mapping,
                            [field.value]: parseInt(val || "-1"),
                          })
                        }
                        placeholder="Select column"
                      />
                      {sampleValues.length > 0 && (
                        <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                          e.g. {sampleValues.join(", ")}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Skip Duplicates */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
                className="rounded border-slate-300 text-brand-accent focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-600">
                Skip duplicates (same name or email)
              </span>
            </label>

            {/* Preview Table */}
            {preview.sampleRows.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-3 py-2 text-left font-semibold">#</th>
                      {preview.headers.map((h, i) => (
                        <th
                          key={i}
                          className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                        >
                          {h}
                          {mappedColumns.has(i) && (
                            <span className="ml-1 text-emerald-300">
                              (
                              {fields.find((f) => mapping[f.value] === i)
                                ?.label || "mapped"}
                              )
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.sampleRows.slice(0, 3).map((row, ri) => (
                      <tr key={ri} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-400">{ri + 1}</td>
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`px-3 py-2 whitespace-nowrap ${
                              mappedColumns.has(ci)
                                ? "text-slate-800 font-medium"
                                : "text-slate-400"
                            }`}
                          >
                            {cell || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.totalRows > 3 && (
                  <div className="px-3 py-2 bg-slate-50 text-xs text-slate-400 border-t border-slate-100">
                    Showing first 3 of {preview.totalRows} rows
                  </div>
                )}
              </div>
            )}

            {/* Required field warning */}
            {!displayNameMapped && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-600">
                  The <strong>Name</strong> field must be mapped to a column
                  before you can import.
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={resetToUpload}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={loading || !displayNameMapped}
                className="px-6 py-2.5 rounded-xl bg-brand hover:bg-brand-hover text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  `Import ${preview.totalRows} ${entityType === "clients" ? "Clients" : "Contacts"}`
                )}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Upload step ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-slate-900">Import your data</h2>
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-400 leading-none">
            Optional
          </span>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-slate-100 to-slate-50 text-slate-500 border border-slate-200 hover:from-slate-200 hover:to-slate-100 hover:text-slate-700 hover:border-slate-300 shadow-sm hover:shadow transition-all"
            >
              <SkipForward className="h-3.5 w-3.5" />
              Skip this step
            </button>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Bring in your existing clients and contacts from a CSV file, or start
          fresh.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Entity type toggle */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {(["clients", "contacts"] as EntityType[]).map((et) => (
          <button
            key={et}
            onClick={() => setEntityType(et)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              entityType === et
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {et === "clients" ? "Clients" : "Contacts"}
          </button>
        ))}
      </div>

      {/* Dropzone */}
      <div
        className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${
          dragging
            ? "border-brand-accent bg-brand-light scale-[1.01]"
            : "border-slate-300 hover:border-brand-accent hover:bg-brand-light/30 bg-slate-50/50"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
            <p className="text-sm font-medium text-slate-600">
              Parsing CSV...
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div
              className={`h-12 w-12 rounded-xl flex items-center justify-center mb-3 transition-colors ${
                dragging
                  ? "bg-brand-light text-brand-accent"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              <Upload className="h-6 w-6" />
            </div>
            <p className="text-sm font-medium text-slate-700">
              {dragging ? "Drop your CSV here" : "Drop your CSV file here or click to browse"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              CSV file up to 10 MB
            </p>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />

      {/* Template download card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <FileSpreadsheet className="h-5 w-5 text-brand" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">
              Need a template?
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Download a ready-to-fill CSV with the correct columns and a sample row.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-brand border-2 border-brand/20 hover:bg-brand-light transition-all flex-shrink-0"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>
        <div className="px-5 pb-4 pt-0">
          <div className="flex flex-wrap gap-1.5">
            {fields.map((f) => (
              <span
                key={f.value}
                className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-100 text-[11px] text-slate-500 font-medium"
              >
                {f.label}
                {f.required && <span className="text-red-400 ml-0.5">*</span>}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Start fresh option */}
      <div className="text-center pt-2">
        <button
          onClick={onImportComplete}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          Or skip and add clients manually later
        </button>
      </div>
    </div>
  );
};

export default ImportDataStep;
